import { FC, useMemo } from 'react';
import If from '@/storybook/components/If/If';
import { ColumnDisplayItem } from '@/types/table/columnConfig';
import ColumnConfigItem from './ColumnConfigItem';
import styles from './DisplayTab.module.css';

type DisplayRow = {
  id: string;
  label: string;
  isDisabled: boolean;
  isVisible: boolean | 'indeterminate';
  isChild: boolean;
  isGroup: boolean;
  section?: string;
};

type GroupedDisplayItems = {
  items: ColumnDisplayItem[];
  visibleChildrenCount: number;
};

const buildItemRow = (displayItem: ColumnDisplayItem): DisplayRow => ({
  id: displayItem.id,
  label: displayItem.label,
  isDisabled: displayItem.isDisabled,
  isVisible: displayItem.isVisible,
  isChild: false,
  isGroup: false,
  section: displayItem.section,
});

const buildChildRow = (displayItem: ColumnDisplayItem): DisplayRow => ({
  id: displayItem.id,
  label: displayItem.label,
  isDisabled: displayItem.isDisabled,
  isVisible: displayItem.isVisible,
  isChild: true,
  isGroup: false,
  section: displayItem.section,
});

interface DisplayTabProps {
  items: ColumnDisplayItem[];
  pinnedItems: ColumnDisplayItem[];
  onToggleVisibility: (columnId: string) => void;
}

const DisplayTab: FC<DisplayTabProps> = ({ items, pinnedItems, onToggleVisibility }) => {
  const totalVisibleCount = useMemo(() => items.filter(displayItem => displayItem.isVisible).length, [items]);
  const pinnedIdSet = useMemo(() => new Set(pinnedItems.map(pinnedItem => pinnedItem.id)), [pinnedItems]);

  const lowerSectionRows = useMemo(() => {
    const groupedItems = new Map<string, GroupedDisplayItems>();
    const rows: DisplayRow[] = [];

    for (const displayItem of items) {
      if (pinnedIdSet.has(displayItem.id)) {
        continue;
      }

      const parentGroup = displayItem.parentGroup;

      if (!parentGroup) {
        rows.push(buildItemRow(displayItem));
        continue;
      }

      const currentGroupItems = groupedItems.get(parentGroup);

      if (currentGroupItems) {
        currentGroupItems.items.push(displayItem);
        currentGroupItems.visibleChildrenCount += displayItem.isVisible ? 1 : 0;
        continue;
      }

      groupedItems.set(parentGroup, {
        items: [displayItem],
        visibleChildrenCount: displayItem.isVisible ? 1 : 0,
      });
    }

    for (const [groupLabel, groupEntry] of groupedItems.entries()) {
      const { items: groupItems, visibleChildrenCount } = groupEntry;
      const allChildrenVisible = visibleChildrenCount === groupItems.length;
      const groupVisibilityState = visibleChildrenCount === 0 ? false : allChildrenVisible ? true : 'indeterminate';
      const isGroupDisabled = allChildrenVisible && visibleChildrenCount === totalVisibleCount;

      rows.push({
        id: groupLabel,
        label: groupLabel,
        isDisabled: isGroupDisabled,
        isVisible: groupVisibilityState,
        isChild: false,
        isGroup: true,
        section: groupItems[0]?.section,
      });

      for (const groupItem of groupItems) {
        rows.push(buildChildRow(groupItem));
      }
    }

    const regularRows: DisplayRow[] = [];
    const sectionedRows: DisplayRow[] = [];

    for (const row of rows) {
      if (row.section) {
        sectionedRows.push(row);
      } else {
        regularRows.push(row);
      }
    }

    return [...regularRows, ...sectionedRows];
  }, [items, pinnedIdSet, totalVisibleCount]);

  const hasPinnedItems = pinnedItems.length > 0;

  return (
    <div className={styles.container} data-testid="display-tab">
      <If condition={hasPinnedItems}>
        <div className={styles.pinnedSection}>
          {pinnedItems.map(pinnedColumn => (
            <ColumnConfigItem
              key={pinnedColumn.id}
              id={`pinned-display-${pinnedColumn.id}`}
              label={pinnedColumn.label}
              isVisible={pinnedColumn.isVisible}
              isDisabled={pinnedColumn.isDisabled}
              showCheckbox
              onToggleVisibility={() => onToggleVisibility(pinnedColumn.id)}
            />
          ))}
        </div>
        <div className={styles.divider} />
      </If>

      <div className={styles.columnList}>
        {lowerSectionRows.map((displayRow, index) => {
          const previousSection = lowerSectionRows[index - 1]?.section;
          const shouldShowSection = displayRow.section && displayRow.section !== previousSection;

          return (
            <div key={displayRow.id} className={styles.row}>
              {shouldShowSection && (
                <div className={styles.section}>
                  <div className={styles.divider} />
                  <p className={styles.sectionTitle}>{displayRow.section}</p>
                </div>
              )}
              <ColumnConfigItem
                id={displayRow.id}
                label={displayRow.label}
                checkedState={displayRow.isVisible}
                isDisabled={displayRow.isDisabled}
                isChild={displayRow.isChild}
                isGroup={displayRow.isGroup}
                showCheckbox
                onToggleVisibility={() => onToggleVisibility(displayRow.id)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DisplayTab;
